{
  "app.title": "QuickHub Installer",
  "app.tagline": "Turn a supported Pixel into a QuickHub Voice Line",
  "app.lang.switch": "العربية",
  "app.signout": "Sign out",
  "app.skip_to_content": "Skip to content",
  "app.status_region": "Installer status",

  "steps.signin": "Sign in",
  "steps.preflight": "Pre-flight",
  "steps.unlock": "Unlock",
  "steps.flash": "Flash",
  "steps.enroll": "Enroll",
  "steps.live": "Live",
  "steps.recovery": "Recovery",
  "steps.progress": "Step {n} of {total}: {name}",
  "steps.group": "Line group",
  "steps.member": "Join group",
  "steps.hoptest": "Hop test",
  "steps.remove": "Remove a phone",

  "common.continue": "Continue",
  "common.back": "Back",
  "common.retry": "Retry",
  "common.cancel": "Cancel",
  "common.copy": "Copy",
  "common.copied": "Copied",
  "common.loading": "Loading…",
  "common.yes": "Yes",
  "common.no": "No",
  "common.unknown": "unknown",
  "common.start_over": "Start over with another phone",
  "common.bytes_of": "{done} of {total}",

  "signin.heading": "Sign in with your QuickHub account",
  "signin.intro": "Use the account of the business that owns the line. The installer only talks to QuickHub's cloud; it never sends your phone's data anywhere else.",
  "signin.dev_token_label": "Access token",
  "signin.dev_token_hint": "Development mode: paste a QuickHub API bearer token. Production uses the normal QuickHub login.",
  "signin.button": "Sign in",
  "signin.signing_in": "Signing in…",

  "models.heading": "Supported phones",
  "models.purchase_spec": "Buy an unlocked / international Google Pixel with OEM unlocking available. US carrier models (Verizon, AT&T, T-Mobile, Xfinity, …) cannot be unlocked and are rejected. Pixel 7 and older are not supported.",
  "models.col.model": "Model",
  "models.col.codename": "Codename",
  "models.col.tier": "Tier",
  "models.col.updates": "Google updates until",
  "models.col.status": "Status",
  "models.active": "Available",
  "models.inactive": "Not accepting installs",
  "models.tier.standard": "Standard",
  "models.tier.budget": "Budget / refurbished — recording + transcription",
  "models.tier.successor": "Successor",
  "models.tier.agent": "Agent tier",

  "browser.heading": "Browser check",
  "browser.ok": "Chrome / Edge on a desktop with WebUSB — good to go.",
  "browser.not_chromium": "This installer needs a Chromium browser (Google Chrome or Microsoft Edge). Firefox and Safari do not support WebUSB.",
  "browser.not_desktop": "Use a desktop or laptop computer. Phones and tablets cannot flash another phone.",
  "browser.no_webusb": "WebUSB is unavailable. Check that USB access is not disabled by policy and that the page is served over https.",
  "browser.insecure": "The page must be loaded over https.",

  "drivers.heading": "USB driver for {os}",
  "drivers.windows": "Install the Google USB Driver, then reconnect the phone. Windows will show it as \"Android Bootloader Interface\".",
  "drivers.windows_link": "Google USB Driver download",
  "drivers.linux": "Add a udev rule so your user can talk to the phone, then unplug and re-plug it:",
  "drivers.macos": "No driver needed on macOS.",
  "drivers.chromeos": "No driver needed on ChromeOS.",
  "drivers.unsupported": "This operating system is not supported by the installer.",
  "drivers.copy_rule": "Copy the commands",

  "preflight.heading": "Connect the phone in fastboot mode",
  "preflight.step1": "Power the phone off completely.",
  "preflight.step2": "Hold Volume Down and press Power until the \"Fastboot Mode\" screen appears (red text on black).",
  "preflight.step3": "Connect it to this computer with a USB data cable. Prefer a port on the computer itself, not a hub.",
  "preflight.connect": "Connect phone",
  "preflight.connecting": "Reading the phone…",
  "preflight.reconnect": "Reconnect phone",
  "preflight.reconnect_hint": "The page was reloaded. Pick the same phone in the browser dialog to continue where you left off.",
  "preflight.device_heading": "Phone",
  "preflight.device.product": "Model code",
  "preflight.device.serial": "Serial",
  "preflight.device.unlocked": "Bootloader unlocked",
  "preflight.device.slot": "Active slot",
  "preflight.device.anti_rollback": "Anti-rollback",
  "preflight.device.bootloader": "Bootloader",
  "preflight.device.baseband": "Radio",
  "preflight.device.unlock_ability": "OEM unlocking allowed",
  "preflight.ok": "{model} detected — supported.",
  "preflight.refused.needs_bootloader_mode": "The phone is in userspace fastboot (fastbootd). Reboot it to the bootloader and try again.",
  "preflight.refused.unsupported_model": "\"{product}\" is not a supported model. Supported: {models}.",
  "preflight.refused.model_inactive": "{model} is not accepting new installs right now.",
  "preflight.refused.carrier_locked": "The bootloader reports that unlocking is not allowed. Either OEM unlocking is off in Developer options (turn it on, reboot to fastboot, retry) or this is a carrier-locked unit, which cannot run QuickHub OS.",
  "preflight.refused.us_carrier_variant": "This looks like a US carrier phone ({carrier}). Carrier units cannot be bootloader-unlocked and cannot run QuickHub OS. Return it and buy an unlocked / international model.",
  "preflight.refused.anti_rollback": "The phone's bootloader is newer than the QuickHub OS build for it (anti-rollback). Wait for the next QuickHub OS release.",
  "preflight.warn.us_carrier_variant": "Warning: bootloader strings look like a US carrier variant ({carrier}). Unlocking may be refused by the phone.",
  "preflight.warn.unlock_ability_unknown": "This bootloader does not report OEM-unlock ability; the unlock step will tell.",
  "preflight.already_unlocked": "The bootloader is already unlocked — the unlock step will be skipped.",

  "sim.heading": "SIM ready?",
  "sim.intro": "QuickHub OS has no eSIM manager (Google's is part of Play Services, which QuickHub OS does not ship). A physical SIM simply works. An eSIM profile keeps working after the install, but only if it was activated on this phone beforehand — so activate it now, on stock Android, and make a test call before you continue.",
  "sim.option.physical": "A physical SIM is inserted in the phone.",
  "sim.option.esim_activated": "The eSIM profile is already activated on this phone and works on stock Android (I made a test call).",
  "sim.consequence": "Consequence for eSIMs: once QuickHub OS is installed, the profile cannot be added, switched or deleted on the phone. To change it later you must restore Google's stock Android (Recovery), change the eSIM there, then install QuickHub OS again. Physical SIMs can be swapped at any time.",
  "sim.confirm_label": "I confirm the SIM in this phone is ready as selected above",
  "sim.button": "Confirm SIM ready",
  "sim.confirmed.physical": "SIM confirmed: physical SIM inserted.",
  "sim.confirmed.esim_activated": "SIM confirmed: eSIM profile activated and tested on stock Android.",
  "sim.esim_note.recovery": "eSIM: a profile can only be added, switched or deleted on stock Android — QuickHub OS has no eSIM manager. To change a phone's eSIM, restore stock, change the profile there, then reflash QuickHub OS. Physical SIMs are unaffected.",
  "sim.esim_note.remove": "If the removed phone uses an eSIM, its profile can only be changed after restoring Google's stock Android (Recovery section, once this removal is done). Physical SIMs can be swapped at any time.",
  "sim.esim_note.stock": "eSIM profiles can be managed on this phone again (Settings › Network & internet › SIMs). If it will run QuickHub OS again, activate or change the eSIM here first and make a test call.",

  "unlock.heading": "Unlock the bootloader",
  "unlock.warning_title": "This erases everything on the phone",
  "unlock.warning_body": "Unlocking wipes all data, accounts and apps. The phone will belong to this QuickHub line afterwards. Make sure nothing on it is needed.",
  "unlock.oem_heading": "First, enable OEM unlocking on the phone (one time)",
  "unlock.oem1": "Boot the phone normally and sign out of any Google account (Settings › Passwords & accounts).",
  "unlock.oem2": "Settings › About phone › tap \"Build number\" seven times.",
  "unlock.oem3": "Settings › System › Developer options › turn on \"OEM unlocking\" (it needs internet once; if it is greyed out, the unit is carrier-locked).",
  "unlock.oem4": "Power off, hold Volume Down + Power to return to Fastboot Mode, reconnect the cable.",
  "unlock.oem_done": "OEM unlocking is on and the phone is back in fastboot",
  "unlock.confirm_label": "Type {word} to confirm the wipe",
  "unlock.confirm_word": "ERASE",
  "unlock.button": "Unlock and erase",
  "unlock.on_phone": "On the phone: use Volume keys to highlight \"Unlock the bootloader\" and press Power. The phone erases itself and returns to fastboot; this page continues automatically.",
  "unlock.waiting": "Waiting for the phone to come back…",

  "flash.heading": "Installing QuickHub OS",
  "flash.heading_stock": "Restoring Google's stock Android",
  "flash.phase.manifest": "Fetching and verifying the signed release manifest",
  "flash.phase.download": "Downloading the image bundle (resumes if interrupted)",
  "flash.phase.flashing": "Writing the phone — do not unplug",
  "flash.phase.locking": "Locking the bootloader",
  "flash.phase.done": "Done",
  "flash.build": "Build {build}",
  "flash.file_progress": "File {n} of {total}: {name}",
  "flash.part_progress": "Writing {name}",
  "flash.start": "Start",
  "flash.resume": "Resume",
  "flash.lock_on_phone": "On the phone: highlight \"Lock the bootloader\" with the Volume keys and press Power.",
  "flash.reboot_note": "The phone reboots a few times during this step. Keep it connected.",
  "flash.leave_unlocked_note": "The bootloader will be left unlocked as requested.",

  "enroll.heading": "Enroll the phone",
  "enroll.intro": "The phone is booting QuickHub OS for the first time. On its Setup screen, scan this code or type the token.",
  "enroll.token_label": "One-time token",
  "enroll.expires": "Expires {when}",
  "enroll.qr_alt": "Enrollment QR code",
  "enroll.request": "Get an enrollment token",
  "enroll.state.pending": "Waiting for the phone to use the token",
  "enroll.state.token_consumed": "Token accepted — the phone is requesting its certificate",
  "enroll.state.enrolled": "Certificate issued — probing capabilities",
  "enroll.state.probed": "Capabilities reported — finishing",
  "enroll.state.live": "Line is live",
  "enroll.state.expired": "Token expired",
  "enroll.state.failed": "Enrollment failed",

  "live.heading": "{group} — phone {position} of {total} is live",
  "live.intro": "The phone is enrolled and reporting to QuickHub. You can close this page.",
  "live.device": "Device {id}",
  "live.caps.heading": "Capabilities",
  "live.caps.capture_downlink": "Capture caller audio",
  "live.caps.capture_uplink": "Capture handset audio",
  "live.caps.uplink_injection": "AI voice agent (audio injection)",
  "live.caps.call_screening_mode": "Call screening mode",
  "live.recording_only": "This phone reports no uplink injection: the line will record and transcribe calls but cannot run the AI voice agent on them.",
  "live.stock_heading": "Stock Android restored",
  "live.stock_intro": "The phone now runs Google's factory image. It is no longer part of a QuickHub line.",
  "live.stock_unlocked": "The bootloader was left unlocked.",
  "live.another": "Install another phone",
  "live.hop_test_passed": "Live hop test: passed and saved to the audit log.",
  "live.hop_test_skipped": "Live hop test: skipped (every phone read back the desired state).",
  "live.forwarding_heading": "This phone's forwarding (read back from the operator)",
  "live.caps.hold_supported": "Hold / unhold",
  "live.caps.call_waiting_delivered": "Waiting calls delivered",
  "live.caps.forwarding_api": "forwarding API",

  "error.heading": "Something went wrong",
  "error.device_disconnected": "The phone disconnected. Check the cable, make sure it is back in Fastboot Mode, then retry.",
  "error.not_in_fastboot": "The selected USB device is not in fastboot mode.",
  "error.webusb_unsupported": "This browser cannot access USB devices.",
  "error.no_device_selected": "No phone was selected in the browser dialog.",
  "error.wrong_device": "That is a different phone than the one this session started with.",
  "error.fastboot_fail": "The phone refused a command: {message}",
  "error.fastboot_protocol": "Unexpected reply from the phone: {message}",
  "error.fastboot_too_large": "An image is too large for this bootloader to accept.",
  "error.manifest_signature": "The release manifest signature is invalid. The download was NOT trusted. Contact support.",
  "error.manifest_unknown_key": "The release manifest was signed with a key this installer does not know. Update the installer.",
  "error.manifest_shape": "The release manifest is malformed: {message}",
  "error.manifest_google_mismatch": "The bundle's bootloader or radio does not match Google's published image. Refusing to flash.",
  "error.manifest_model_mismatch": "This bundle does not fit this phone: {message}",
  "error.download_http": "Download failed: {message}",
  "error.download_size": "Download returned the wrong amount of data.",
  "error.download_sha256": "A downloaded image failed its integrity check and was discarded. Retry to download it again.",
  "error.download_aborted": "Download cancelled.",
  "error.unauthenticated": "Your session expired. Sign in again.",
  "error.api": "QuickHub cloud error: {message}",
  "error.unlock_not_confirmed": "Confirm the wipe first.",
  "error.sim_not_confirmed": "Confirm the SIM checklist first: a physical SIM is inserted, or the eSIM profile is activated and tested on stock Android.",
  "error.carrier_locked": "The phone refused to unlock. OEM unlocking is off, or this is a carrier-locked unit.",
  "error.unlock_declined": "The phone is still locked. Choose \"Unlock the bootloader\" on the phone, then retry.",
  "error.enrollment_expired": "The enrollment token expired. Request a new one.",
  "error.enrollment_failed": "Enrollment failed: {message}",
  "error.unexpected": "Unexpected error: {message}",
  "error.wrong_step": "Internal error: {message}",
  "error.guard_group_full": "A line group has at most 5 phones (operators cap forwarding hops). Replace a phone instead of adding one.",
  "error.guard_same_phone_hop": "That would point a SIM at another SIM in the same phone; a phone still carries only one call. {message}",
  "error.guard_loop": "That would create a forwarding loop: {message}",
  "error.guard_invalid_number": "Enter the number in E.164 form (+9715xxxxxxxx): {message}",
  "error.guard_licence_not_attested": "Tick the licence attestation and enter your name.",
  "error.guard_position_taken": "{message}",
  "error.guard_position_unknown": "{message}",
  "error.api_group_full": "The cloud refused: the group already has 5 phones.",
  "error.api_same_phone_hop": "The cloud refused: this phone already holds a member of the group.",
  "error.api_loop": "The cloud refused: that number is already a member (loop).",
  "error.api_licence_not_attested": "The cloud refused: the licence attestation is missing.",
  "error.chain_job_failed": "The cloud could not program the forwarding chain: {message}",
  "error.forwarding_out_of_sync": "A phone read back a forwarding state that does not match: {message}. Run reconciliation again; if it keeps failing, check the plan has call forwarding enabled.",
  "error.hop_test_failed": "The live hop test did not pass: {message}",
  "error.hop_test_required": "The live hop test is mandatory: a phone reported an unverified forwarding state.",
  "error.not_live_yet": "The cloud has not marked the line live yet: {message}",
  "error.member_not_removed": "{message}",

  "logs.heading": "Diagnostics",
  "logs.download": "Download log file",
  "logs.upload": "Send logs to QuickHub support",
  "logs.uploaded": "Sent. Support reference: {ticket}",
  "logs.entries": "{n} log entries",
  "logs.session": "Session {id}",

  "group.heading": "Choose the line group for this phone",
  "group.intro": "A line group is 1–5 phones, each with its own business SIM, chained by the operator's call forwarding so a second caller to the main number rings the next free phone. A single phone is a group of one. Pick the group this phone joins, or create a new one — the new group's main number is the number customers dial and this phone will hold it.",
  "group.none": "No line groups yet — create one below.",
  "group.members_count": "{n} of {max} phones",
  "group.status.ok": "in sync",
  "group.status.degraded": "degraded",
  "group.status.unverified": "unverified",
  "group.create_name": "New group name",
  "group.create_number": "Main number (E.164)",
  "group.create_button": "Create group",
  "group.create_hint": "The main number is the SIM in this phone. Type it with the country code, e.g. +9715xxxxxxxx.",
  "group.sim_req.heading": "What every phone in a group needs",
  "group.sim_req.licence": "Its own business SIM whose number is registered under the company's commercial licence (required for any number used for marketing calls).",
  "group.sim_req.forwarding": "A plan with call forwarding enabled (du and e& business plans generally include it — confirm per plan).",
  "group.sim_req.billing": "Acceptance that forwarded call legs may be billed to the forwarding number as outgoing calls.",
  "group.sim_req.visible": "Outbound calls from a phone show that phone's number, so every member number is one customers will see.",
  "group.plan_create": "\"{group}\" has no phone yet. This phone becomes member 1 and holds the main number {number}.",
  "group.plan_add": "This phone becomes member {position} of \"{group}\". Phone {previous} will forward busy / no-reply / unreachable calls to it and turn call waiting off; this phone becomes the last member with call waiting on.",
  "group.plan_full": "\"{group}\" already has {max} phones, the maximum an operator forwarding chain allows. Replace a phone instead, or move to a SIP trunk for more concurrency.",
  "group.use_create": "Use this group (member 1)",
  "group.use_add": "Add this phone to the group",
  "group.replace_heading": "Replace an existing phone (broken or upgraded)",
  "group.replace_hint": "The new phone takes the same position and SIM. The previous phone is only re-pointed if the number changes. If the old phone is dead, its forwarding stays valid — it lives with the operator.",
  "group.replace_position": "Phone to replace",
  "group.replace_button": "Replace this member with the connected phone",
  "group.position_pick": "Choose…",
  "group.position_option": "Phone {position} · {number} · {role}",
  "group.recovery_heading": "Recovery for an existing phone",
  "group.recovery_hint": "Both options erase the phone. Reflash re-enrolls it at the same position with the same SIM. Restoring stock removes QuickHub's verified-boot key.",
  "group.recovery_position": "Which phone (position in the group)",
  "group.recovery_leave_unlocked": "Leave the bootloader unlocked (for returning the phone)",
  "group.recovery_reflash": "Reflash QuickHub OS",
  "group.recovery_stock": "Restore Google's stock Android",
  "group.recovery_remove_first": "A phone that is still an enrolled member cannot be wiped: forwarding lives on the operator's network, so remove it from the group first (below) or calls keep flowing to a phone that no longer answers.",
  "group.remove_heading": "Remove a phone from the group",
  "group.remove_hint": "No phone needs to be connected. The cloud re-points the previous phone past this one first, then erases this phone's forwarding and waits for read-backs. Only then is a wipe or return allowed.",
  "group.remove_position": "Phone to remove",
  "group.remove_force": "The phone is dead or unreachable (I will clear its forwarding by dialling ##002# on that SIM from any phone)",
  "group.remove_button": "Remove this phone",

  "fwd.member": "Phone {position} · {number} · {role}",
  "fwd.role.main": "main number",
  "fwd.role.middle": "middle",
  "fwd.role.last": "last (call waiting on)",
  "fwd.source": "Read-back source: {source}",
  "fwd.source.network_query": "network query (verified)",
  "fwd.source.unverified": "UNVERIFIED — the phone could not read the operator's state back",
  "fwd.source.none": "no read-back yet",
  "fwd.readback_at": "read at {when}",
  "fwd.col.rule": "Rule",
  "fwd.col.desired": "Desired",
  "fwd.col.actual": "Phone read back",
  "fwd.col.ok": "Match",
  "fwd.rule.busy": "Forward on busy",
  "fwd.rule.no_reply": "Forward on no reply",
  "fwd.rule.not_reachable": "Forward when unreachable",
  "fwd.rule.unconditional": "Unconditional forward",
  "fwd.rule.call_waiting": "Call waiting",

  "sync.in_sync": "in sync",
  "sync.out_of_sync": "OUT OF SYNC",
  "sync.unverified": "unverified",
  "sync.repairing": "repairing",

  "job.state.pending": "Waiting for the cloud to start",
  "job.state.reprogramming": "Reprogramming forwarding on the phones",
  "job.state.awaiting_readback": "Waiting for the phones to read the operator's state back",
  "job.state.done": "Chain programmed and verified",
  "job.state.failed": "Chain programming failed",
  "job.step": "Phone {position}: {action} — {state}",
  "job.action.program": "program forwarding",
  "job.action.forwarding_set": "set forwarding",
  "job.action.call_waiting_set": "set call waiting",
  "job.action.readback": "read back",
  "job.action.erase_forwarding": "erase forwarding",
  "job.action.repoint": "re-point to next member",
  "job.action.config": "send group config",
  "job.step_state.pending": "pending",
  "job.step_state.running": "in progress",
  "job.step_state.sent": "sent",
  "job.step_state.awaiting_readback": "awaiting read-back",
  "job.step_state.done": "done",
  "job.step_state.failed": "failed",
  "job.step_state.skipped": "skipped",

  "member.heading": "Join \"{group}\" as phone {position}",
  "member.intro_create": "This phone becomes member 1 and holds the main number {number}. It is also the last member for now: no forwarding, call waiting on.",
  "member.intro_add": "This phone becomes member {position}. The cloud will make phone {previous} forward to it and turn phone {previous}'s call waiting off, then set this phone up as the last member.",
  "member.intro_replace": "This phone replaces member {position}, keeping the same position and SIM. The previous phone is re-pointed only if the number changes.",
  "member.number_label": "This phone's SIM number (E.164)",
  "member.number_hint": "The installer cannot read the SIM over fastboot. The number is shown on the phone's Setup screen (and in the QuickHub dashboard once it enrolls) — copy it here and confirm.",
  "member.number_prefilled": "Prefilled from what the phone reported at enrollment. Confirm it matches the SIM in the phone.",
  "member.attest_heading": "Licence attestation",
  "member.attest_label": "I confirm that {number} is registered under this business's commercial licence and the plan has call forwarding enabled.",
  "member.attested_by": "Your name (recorded with the attestation)",
  "member.attest_hint": "UAE telemarketing rules require every number used for business calls to be registered under the company licence. The attestation is stored in the group's audit log.",
  "member.submit": "Add to the group",
  "member.submit_replace": "Replace the member",
  "member.programming": "Programming the chain for {number}…",
  "member.expect_previous": "Phone {position}: forwarding on busy, no reply and unreachable → phone {next}; call waiting off.",
  "member.expect_new": "Phone {position}: no forwarding; call waiting on (it is now the last member).",
  "member.expect_readback": "Waiting for both phones to read the operator's state back and match.",
  "member.repair": "Run reconciliation again",

  "hoptest.heading": "Verify the chain with a live call",
  "hoptest.readback_heading": "What the phones read back",
  "hoptest.required": "A phone could not verify the operator's forwarding state (unverified). The live hop test is mandatory before this line goes live.",
  "hoptest.optional": "Every phone read back the desired state. The live hop test is optional but recommended after a chain change.",
  "hoptest.instructions": "Enter the customer's own mobile number. The cloud will make the phones busy one hop at a time using the QuickHub test line; when the page says so, call the main number {main} from that mobile and let it ring.",
  "hoptest.caller_label": "Caller's mobile number (not a group member)",
  "hoptest.no_reply_label": "Also test no-reply forwarding (leave the target phone unanswered for the no-reply timer)",
  "hoptest.start": "Start the hop test",
  "hoptest.skip": "Skip the test",
  "hoptest.running": "Test running — follow the instructions below",
  "hoptest.passed": "Hop test passed",
  "hoptest.failed": "Hop test failed",
  "hoptest.call_now": "Call {main} from {caller} now, and again for each hop when the previous one is marked.",
  "hoptest.col.hop": "Hop",
  "hoptest.col.expected": "Should ring",
  "hoptest.col.rang": "Rang",
  "hoptest.col.ttr": "Time to ring",
  "hoptest.col.forwarded_from": "Forwarded from",
  "hoptest.col.result": "Result",
  "hoptest.member": "phone {position} ({number})",
  "hoptest.outcome.pending": "waiting for your call…",
  "hoptest.outcome.ok": "OK",
  "hoptest.outcome.wrong_member": "wrong phone rang",
  "hoptest.outcome.no_ring": "nothing rang",
  "hoptest.no_reply_row": "No reply",
  "hoptest.audit": "Saved to the group's audit log at {when} ({action}):",
  "hoptest.finish": "Finish — mark the line live",
  "hoptest.rerun": "Run the test again",

  "remove.heading": "Removing phone {position} from \"{group}\"",
  "remove.intro": "Forwarding lives on the operator's network, not the phone, so the order matters:",
  "remove.order1": "The previous phone is re-pointed past this one (or, if this was the last phone, the previous one becomes last: forwarding off, call waiting on).",
  "remove.order2": "This phone erases its own forwarding and reads the result back.",
  "remove.order2_force": "This phone is dead, so YOU erase its forwarding: dial ##002# on that SIM from any phone.",
  "remove.order3": "Only then may the phone be wiped, restored to stock, or returned.",
  "remove.dead_heading": "Clear forwarding on the removed SIM",
  "remove.dead_body": "Put the SIM in any phone and dial:",
  "remove.dead_hint": "This is the standard GSM code that erases every call-forwarding rule on that number. The phone shows a confirmation from the operator.",
  "remove.dead_confirm_label": "I dialled {code} on that SIM and the operator confirmed forwarding is erased",
  "remove.dead_confirm_button": "Confirm and close the removal",
  "remove.done": "Phone {position} is out of the group and the chain is verified.",
  "remove.done_next": "You can now wipe it or restore stock Android from the Recovery section.",
  "remove.back": "Back to line groups"
}
